import React from 'react';
import { motion } from 'framer-motion';
import { FiLayout, FiTrendingUp, FiCheckCircle, FiArrowRight, FiCode, FiPackage, FiShoppingBag, FiSettings } from 'react-icons/fi';
import { Link } from 'react-router-dom';
import { imageConfig } from '../../config/images';
import { useDocumentTitle } from '../../hooks/useDocumentTitle';

const WordPressPage = () => {
  useDocumentTitle(
    'WordPress Development Services | India\'s Expert Team for Custom Websites',
    'A reliable wordpress development company in india that creates secure, custom, and easy-to-manage websites designed for strong performance.'
  );
  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 overflow-hidden bg-gradient-to-br from-orange-600 via-red-600 to-orange-700">
        <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0zNiAxOGMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnptMCAzNmMzLjMxNCAwIDYgMi42ODYgNiA2cy0yLjY4NiA2LTYgNi02LTIuNjg2LTYtNiAyLjY4Ni02IDYtNnoiIHN0cm9rZT0iI2ZmZiIgc3Ryb2tlLW9wYWNpdHk9Ii4xIi8+PC9nPjwvc3ZnPg==')] opacity-10"></div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="grid md:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.8 }}
            >
              <h1 className="text-6xl font-bold text-white mb-6">
                WordPress Development
              </h1>
              <p className="text-2xl text-white/90 mb-8 leading-relaxed">
                Power your business with custom WordPress solutions. From stunning websites to powerful e-commerce stores, we build WordPress sites that drive results.
              </p>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
                >
                  <span>Start Your Project</span>
                  <FiArrowRight className="w-5 h-5" />
                </Link>
                <Link
                  to="/contact"
                  className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
                >
                  <span>View Portfolio</span>
                </Link>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* What is WordPress Development */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">What is WordPress Development?</h2>
            <p className="text-xl text-gray-600 leading-relaxed">
              WordPress powers 43% of all websites worldwide. Our expert developers create custom WordPress solutions tailored to your business needs - from elegant corporate websites to feature-rich e-commerce stores. We combine beautiful design with powerful functionality.
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiLayout className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Custom Themes</h3>
              <p className="text-gray-600 leading-relaxed">
                Fully custom WordPress themes designed to match your brand identity and business goals. Responsive, fast-loading, and SEO-optimized.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiPackage className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Plugin Development</h3>
              <p className="text-gray-600 leading-relaxed">
                Custom plugin development to add unique functionality to your WordPress site. Extend capabilities beyond standard features.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiShoppingBag className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">WooCommerce</h3>
              <p className="text-gray-600 leading-relaxed">
                Complete WooCommerce setup and customization for online stores. Payment gateways, shipping, inventory, and product management.
              </p>
            </div>

            <div className="bg-white rounded-2xl shadow-lg p-8 hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-2">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-16 h-16 rounded-xl flex items-center justify-center mb-6">
                <FiSettings className="text-3xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Maintenance & Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Ongoing maintenance, security updates, backups, and technical support to keep your WordPress site running smoothly.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Our Process */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Our Development Process</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              From concept to launch - a systematic approach to WordPress excellence
            </p>
          </div>

          <div className="max-w-5xl mx-auto space-y-8">
            {[
              {
                step: '01',
                title: 'Discovery & Planning',
                description: 'Understand your business goals, target audience, and project requirements. Create detailed specifications and site architecture.',
                icon: FiLayout
              },
              {
                step: '02',
                title: 'Design & Prototyping',
                description: 'Design custom mockups and interactive prototypes. Refine based on your feedback until perfect.',
                icon: FiCode
              },
              {
                step: '03',
                title: 'Development',
                description: 'Build your WordPress site with clean, maintainable code. Implement all features and functionality.',
                icon: FiSettings
              },
              {
                step: '04',
                title: 'Content Integration',
                description: 'Add your content, optimize images, configure SEO settings, and ensure everything displays perfectly.',
                icon: FiLayout
              },
              {
                step: '05',
                title: 'Testing & Quality Assurance',
                description: 'Comprehensive testing across devices and browsers. Fix any bugs and optimize performance.',
                icon: FiCheckCircle
              },
              {
                step: '06',
                title: 'Launch & Training',
                description: 'Deploy to production, provide training on WordPress admin, and offer ongoing support.',
                icon: FiTrendingUp
              }
            ].map((process, index) => (
              <div key={index} className="flex gap-6 items-start group">
                <div className="flex-shrink-0">
                  <div className="bg-gradient-to-br from-orange-500 to-red-600 text-white text-2xl font-bold w-16 h-16 rounded-xl flex items-center justify-center shadow-lg group-hover:scale-110 transition-all duration-300">
                    {process.step}
                  </div>
                </div>
                <div className="flex-grow bg-gray-50 rounded-xl p-6 group-hover:shadow-xl transition-all duration-300">
                  <h3 className="text-2xl font-bold text-gray-800 mb-3">{process.title}</h3>
                  <p className="text-gray-600 leading-relaxed">{process.description}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pricing Packages */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">WordPress Packages</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Flexible solutions for every business size and budget
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {[
              {
                name: 'Basic',
                price: '₹25,000',
                duration: 'one-time',
                popular: false,
                features: [
                  'Up to 5 Pages',
                  'Mobile Responsive',
                  'Contact Form',
                  'Basic SEO Setup',
                  'Social Media Integration',
                  '1 Month Support'
                ]
              },
              {
                name: 'Professional',
                price: '₹50,000',
                duration: 'one-time',
                popular: true,
                features: [
                  'Up to 15 Pages',
                  'Custom Design',
                  'WooCommerce Setup',
                  'Advanced SEO',
                  'Blog Integration',
                  'Speed Optimization',
                  'Security Setup',
                  '3 Months Support'
                ]
              },
              {
                name: 'Enterprise',
                price: 'Custom',
                duration: 'pricing',
                popular: false,
                features: [
                  'Unlimited Pages',
                  'Fully Custom Theme',
                  'Custom Plugins',
                  'Advanced E-commerce',
                  'API Integrations',
                  'Multi-language',
                  '6 Months Support',
                  'Priority Development'
                ]
              }
            ].map((pkg, index) => (
              <div 
                key={index}
                className={`relative bg-white rounded-2xl shadow-lg p-8 transform hover:scale-105 transition-all duration-300 ${
                  pkg.popular ? 'ring-4 ring-orange-500 shadow-2xl' : ''
                }`}
              >
                {pkg.popular && (
                  <div className="absolute -top-4 left-1/2 transform -translate-x-1/2">
                    <span className="bg-gradient-to-r from-orange-500 to-red-600 text-white px-6 py-2 rounded-full text-sm font-bold shadow-lg">
                      MOST POPULAR
                    </span>
                  </div>
                )}
                
                <h3 className="text-2xl font-bold text-gray-800 mb-4">{pkg.name}</h3>
                <div className="mb-6">
                  <span className="text-5xl font-bold text-gray-800">{pkg.price}</span>
                  <span className="text-gray-600 ml-2">{pkg.duration}</span>
                </div>
                
                <ul className="space-y-4 mb-8">
                  {pkg.features.map((feature, fIndex) => (
                    <li key={fIndex} className="flex items-start gap-3">
                      <FiCheckCircle className="text-orange-600 flex-shrink-0 mt-1" />
                      <span className="text-gray-600">{feature}</span>
                    </li>
                  ))}
                </ul>
                
                <Link
                  to="/contact"
                  className={`block w-full text-center py-4 rounded-lg font-semibold transition-all duration-300 ${
                    pkg.popular 
                      ? 'bg-orange-600 hover:bg-orange-700 text-white shadow-lg' 
                      : 'bg-gray-100 hover:bg-gray-200 text-gray-800'
                  }`}
                >
                  {pkg.price === 'Custom' ? 'Contact Sales' : 'Get Started'}
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Why Choose Our WordPress Development?</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Expert developers delivering quality and performance
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8">
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiCode className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Clean Code</h3>
              <p className="text-gray-600 leading-relaxed">
                Following WordPress coding standards for maintainable, secure, and scalable websites that perform flawlessly
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiTrendingUp className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">SEO Optimized</h3>
              <p className="text-gray-600 leading-relaxed">
                Built-in SEO best practices with fast loading speeds, clean URLs, and proper schema markup
              </p>
            </div>
            <div className="text-center group">
              <div className="bg-gradient-to-br from-orange-500 to-red-600 w-20 h-20 rounded-2xl flex items-center justify-center mx-auto mb-6 transform group-hover:scale-110 transition-all duration-300 shadow-lg group-hover:shadow-2xl">
                <FiSettings className="text-4xl text-white" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-4">Ongoing Support</h3>
              <p className="text-gray-600 leading-relaxed">
                Dedicated support team available for updates, maintenance, and any technical assistance you need
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-20 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-5xl font-bold text-gray-800 mb-8">Frequently Asked Questions</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Common questions about WordPress development
            </p>
          </div>

          <div className="max-w-4xl mx-auto space-y-6">
            {[
              {
                question: "Why choose WordPress over other platforms?",
                answer: "WordPress powers 43% of all websites due to its flexibility, extensive plugin ecosystem, SEO-friendly architecture, and ease of content management. It's perfect for businesses that want control over their website without technical complexity."
              },
              {
                question: "How long does WordPress development take?",
                answer: "Timeline varies by complexity. A basic website takes 2-3 weeks, professional sites 4-6 weeks, and enterprise projects 8-12 weeks. We'll provide a detailed timeline during the planning phase based on your specific requirements."
              },
              {
                question: "Will I be able to update content myself?",
                answer: "Absolutely! WordPress is known for its user-friendly content management system. We'll provide comprehensive training and documentation so you can easily update text, images, and pages without technical knowledge."
              },
              {
                question: "Is WordPress secure?",
                answer: "Yes, when properly maintained. We implement security best practices including regular updates, security plugins, SSL certificates, secure hosting, and regular backups. We also offer ongoing security monitoring and maintenance packages."
              },
              {
                question: "Can you migrate my existing website to WordPress?",
                answer: "Yes! We specialize in website migrations from various platforms to WordPress. We'll carefully transfer your content, maintain SEO rankings, and ensure a smooth transition with minimal downtime."
              },
              {
                question: "What's included in ongoing support?",
                answer: "Our support packages include WordPress core updates, plugin updates, security monitoring, regular backups, bug fixes, minor content updates, and technical assistance. We offer different support levels based on your needs."
              },
              {
                question: "Do you provide hosting?",
                answer: "We can recommend reliable WordPress hosting providers and help with setup, or work with your existing hosting. We offer managed WordPress hosting packages that include hosting, security, backups, and updates."
              },
              {
                question: "Can you integrate third-party tools?",
                answer: "Yes! We integrate various tools including payment gateways, CRM systems, email marketing platforms, analytics tools, booking systems, and custom APIs. Let us know your requirements and we'll make it work."
              }
            ].map((faq, index) => (
              <details key={index} className="bg-white rounded-xl shadow-lg p-6 group hover:shadow-2xl transition-all duration-300">
                <summary className="text-xl font-bold text-gray-800 cursor-pointer flex items-center justify-between group-hover:text-orange-600 transition-colors">
                  {faq.question}
                  <FiArrowRight className="transform group-open:rotate-90 transition-transform text-orange-600" />
                </summary>
                <p className="mt-4 text-gray-600 leading-relaxed">
                  {faq.answer}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gradient-to-r from-orange-600 to-red-700 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-5xl font-bold mb-8">Ready to Build Your WordPress Site?</h2>
          <p className="text-2xl mb-12 text-white/90 max-w-3xl mx-auto">
            Let's create a powerful WordPress website that drives your business forward
          </p>
          <div className="flex flex-wrap gap-4 justify-center">
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 bg-white text-orange-600 px-8 py-4 rounded-full font-semibold hover:bg-orange-500 hover:text-white transition-all duration-300 shadow-lg hover:shadow-2xl transform hover:scale-105"
            >
              <span>Start Your Project</span>
              <FiArrowRight className="w-5 h-5" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center space-x-2 border-2 border-white text-white px-8 py-4 rounded-full font-semibold hover:bg-white hover:text-orange-600 transition-all duration-300"
            >
              <span>View Portfolio</span>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default WordPressPage;
